import { isValid } from "date-fns";
import type { OnboardingAnswers } from "../types/onboarding";

/**
 * Converts date components to a Date object
 */
export function createDateFromComponents(
	day: string,
	month: string,
	year: string,
): Date | null {
	if (!day || !month || !year) return null;

	const dayNum = parseInt(day, 10);
	const monthNum = parseInt(month, 10);
	const yearNum = parseInt(year, 10);

	if (Number.isNaN(dayNum) || Number.isNaN(monthNum) || Number.isNaN(yearNum))
		return null;

	const date = new Date(yearNum, monthNum - 1, dayNum);
	return isValid(date) ? date : null;
}

/**
 * Extracts date components from OnboardingAnswers
 */
export function getDateFromAnswers(answers: OnboardingAnswers): Date | null {
	return createDateFromComponents(
		answers.dayOfBirth || "",
		answers.monthOfBirth || "",
		answers.yearOfBirth || "",
	);
}

/**
 * Updates OnboardingAnswers with date components from a Date object
 */
export function updateAnswersWithDate(
	_answers: OnboardingAnswers,
	date: Date | null,
	onChange: (field: string, value: string) => void,
): void {
	if (date && isValid(date)) {
		onChange("dayOfBirth", String(date.getDate()));
		onChange("monthOfBirth", String(date.getMonth() + 1));
		onChange("yearOfBirth", String(date.getFullYear()));
	} else {
		onChange("dayOfBirth", "");
		onChange("monthOfBirth", "");
		onChange("yearOfBirth", "");
	}
}

/**
 * Combines first and last name for display
 */
export function combineFullName(firstName: string, lastName: string): string {
	const capitalizedFirst =
		firstName.charAt(0).toUpperCase() + firstName.slice(1);
	const capitalizedLast = lastName
		? ` ${lastName.charAt(0).toUpperCase()}${lastName.slice(1)}`
		: "";

	return `${capitalizedFirst}${capitalizedLast}`;
}

/**
 * Splits a full name into first and last name components
 */
export function splitFullName(fullName: string): {
	firstName: string;
	lastName: string;
} {
	const parts = fullName.trim().split(" ");
	const firstName = parts[0] || "";
	const lastName = parts.slice(1).join(" ");

	return { firstName, lastName };
}
